import PropTypes from 'prop-types';
import { useState } from 'react';
import styled from '@emotion/styled';
import P2 from '@base/P2/P2';
import H4 from '@base/H4/H4';
import BaseImage from '@base/Image';
import Divider from '@base/Divider/Divider';
import Paper from '@base/Paper/Paper';
import Dynamic from '@base/Dynamic/Dynamic';
import { segment } from '@actions/analytics';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { nFormatter } from '@utils/textHelper';
import { useIsMobile } from '@stores/AppCommonData';
import { EDUCATOR_HATS_VIEWED } from '@constants/segment';
import useEducatorDetailsSegmentData from '@hooks/useEducatorDetailsSegmentData';
import { emptyList } from '@constants/empty';
import useProfile from '@api/hooks/profile/useProfile';
import useLearnerMileStonesInfo from '@api/hooks/profile/useLearnerMileStonesInfo';

const EducatorDedications = Dynamic(() => import('./EducatorDedications'));

const Stats = styled(Paper)`
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_4B};
  display: flex;
  align-items: flex-end;
  cursor: ${({ $isEducatorOrIsModerator }) =>
    $isEducatorOrIsModerator ? 'pointer' : 'default'};
  ${mediaQuery.uptoMobile} {
    grid-column: span 10;
    margin-top: 8px;
    padding: ${Spacings.SPACING_3B};
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
`;

const Container = styled.div`
  display: flex;
  align-items: center;
`;

const Value = styled(H4)`
  line-height: ${Fonts.PARA_LINE_HEIGHT};
  margin-bottom: ${Spacings.SPACING_1B};
  color: var(--color-text-primary);
  ${mediaQuery.uptoMobile} {
    margin-bottom: 0;
    font-size: 18px;
    line-height: 150%;
  }
`;

const Label = styled(P2)`
  margin-left: ${Spacings.SPACING_2B};
  margin-bottom: ${Spacings.SPACING_1B};
  color: var(--color-text-primary);
  ${mediaQuery.uptoMobile} {
    margin-bottom: 0;
    margin-left: ${Spacings.SPACING_1B};
    color: var(--color-text-secondary);
  }
`;

const StyledDivider = styled(Divider)`
  margin-left: ${Spacings.SPACING_4B};
  margin-right: ${Spacings.SPACING_4B};
  height: ${Spacings.SPACING_8B};
  width: ${Spacings.SPACING_1};
  ${mediaQuery.uptoMobile} {
    margin: 0;
    background-color: var(--color-divider);
  }
`;

const Hats = styled.div`
  display: flex;
  margin-left: ${Spacings.SPACING_4B};
  flex-direction: row-reverse;
  ${mediaQuery.uptoMobile} {
    margin-left: 0;
    margin-right: ${Spacings.SPACING_4B};
  }
`;

const HatCount = styled.div`
  height: 32px;
  width: 32px;
  background: var(--color-i-blue);
  border: 2px solid var(--color-i-white);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  color: var(--color-i-white);
  margin-left: -${Spacings.SPACING_4B};
  font-weight: bold;
`;

const Hat = styled.div`
  margin-left: -${Spacings.SPACING_4B};
`;

const dedicationsLimit = 5;
const reverse = (array) => [...(array || [])].reverse();

const EducatorDedicationStats = ({ username, trimInMobile = true }) => {
  const isMobile = useIsMobile();
  const { profileData: profileInfo } = useProfile(username);

  const { learnerMileStonesInfo: learnerAchievedMilestones = [] } =
    useLearnerMileStonesInfo(username);

  const segmentData = useEducatorDetailsSegmentData({
    educatorProfile: profileInfo
  });

  const learnerAchievedHats = learnerAchievedMilestones?.achieved?.map(
    (milestone) => ({
      hat: milestone.milestone
    })
  );

  const { dedications: educDedications, isEducator, isModerator } = profileInfo;
  const dedications = educDedications || emptyList;

  const totalLearnerMilestones = learnerAchievedHats?.length;
  const milestonesToShow = learnerAchievedHats;

  const [showDedications, setShowDedications] = useState(false);

  const totalDedications = dedications.reduce(
    (totalHats, dedication) => totalHats + dedication.count,
    0
  );
  const educatorDedications =
    isMobile && trimInMobile && dedications.length > dedicationsLimit
      ? dedications.slice(0, dedicationsLimit)
      : dedications;
  const remainingDedications = dedications.length - educatorDedications.length;
  let label = 'Knowledge Hats';
  let value = nFormatter(totalLearnerMilestones);
  if (isEducator) {
    label = 'Dedications';
    value = nFormatter(totalDedications);
  }

  const isEducatorOrIsModerator = isEducator || isModerator;

  const onClickDedications = () => {
    setShowDedications(true);
    segment.track(EDUCATOR_HATS_VIEWED, { ...segmentData });
  };
  const onCloseDedicationsDrawer = () => setShowDedications(false);

  const hasDedications = dedications.length > 0;

  if (
    ((isEducator && totalDedications > 0) ||
      (!isEducator && totalLearnerMilestones > 0)) &&
    value
  )
    return (
      <>
        <Stats
          onClick={isEducatorOrIsModerator ? onClickDedications : undefined}
          $isEducatorOrIsModerator={isEducatorOrIsModerator}
        >
          <Container>
            <Value>{value}</Value>
            <Label>{label}</Label>
          </Container>
          {hasDedications && (
            <>
              <StyledDivider />
              <Hats>
                {reverse(educatorDedications).map(
                  ({ hat: educatorHat = {} }) => {
                    const { hatIcon, value: key } = educatorHat;
                    if (hatIcon)
                      return (
                        <Hat key={key}>
                          <BaseImage src={hatIcon} height={32} width={32} />
                        </Hat>
                      );
                    return null;
                  }
                )}
                {remainingDedications > 0 && (
                  <HatCount>{`+${remainingDedications}`}</HatCount>
                )}
              </Hats>
            </>
          )}
        </Stats>
        {showDedications && (
          <EducatorDedications
            showDedications={showDedications}
            dedications={isEducator ? dedications : milestonesToShow}
            username={username}
            onCloseDedications={onCloseDedicationsDrawer}
          />
        )}
      </>
    );
  return null;
};
export default EducatorDedicationStats;

EducatorDedicationStats.propTypes = {
  username: PropTypes.string.isRequired
};
